import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { trackContentLike } from '../../lib/gaEvents';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { reviewsApi } from '../../api/reviews';
import { commentApi } from '../../api/comment';
import { useAlbumDetail, useAlbumDetailBySlug } from './hooks/useAlbumDetail';
import { useQueryClient } from '@tanstack/react-query';
import {
  useDeleteReview,
  useReviewById,
  useToggleReviewBookmark,
  useToggleReviewLike,
} from './hooks/useAlbumReviews';
import { Bookmark, Favorite, FavoriteBorderOutlined } from '@mui/icons-material';
import useMyReviewActions from './hooks/useMyReviewActions';
import { useUserStore } from '../../stores/userStore';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import { saveCurrentPathAndRedirectToLoginWithScroll } from '../../utils/authUtils';
import PageLayout from '../../components/layout/PageLayout';
import ProgressBar from '../../components/common/ProgressBar';
import AlbumCover from '../../components/common/AlbumCover';
import AlbumTitleLink from '../../components/common/AlbumTitleLink';
import ArtistLink from '../../components/common/ArtistLink';
import MyReviewForm from './components/my-review/MyReviewForm';
import CommentSection from '../../components/comment/CommentSection';
import SidebarLayout from '../../components/layout/SidebarLayout';
import AdminDeleteButton from '../../components/button/AdminDeleteButton';
import InFeedBoxAd from '../../components/ad/InFeedBoxAd';
import RatingReadOnly from '../../components/common/RatingReadOnly';
import BookmarkButton from '../../components/button/BookmarkButton';
import ShareButton from '../../components/button/ShareButton';
import EditDeleteMenuButton from '../../components/button/EditDeleteMenuButton';
import GradeIcon from '../../components/common/GradeIcon';
import defaultUserThumbnail from '../../assets/default_user_thumbnail.svg';
import favoriteIcon from '../../assets/favorite_primary.svg';
import favoriteOutlinedIcon from '../../assets/favorite_outlined_b3.svg';
import theme from '../../theme';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';

const UserReview = () => {
  const params = useParams();
  const albumSlug = params.albumSlug;
  const paramSource = params.albumSource;
  const paramId = params.sourceId;
  const { reviewId, commentId } = params;
  const commentScrollId = commentId ? parseInt(commentId, 10) : null;
  const navigate = useNavigate();
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const muiTheme = useTheme();
  const isMdUp = useMediaQuery(muiTheme.breakpoints.up('md'));
  const { resolvedMode } = useColorScheme();
  const isDarkMode = resolvedMode === 'dark';
  const { user } = useUserStore();
  const isAdmin = useUserStore((state) => state.isAdmin);
  const isLogin = useUserStore((state) => state.isLogin);

  const slugResult = useAlbumDetailBySlug(albumSlug);
  const { data: directAlbumInfo, isLoading: directLoading } = useAlbumDetail(paramSource, paramId);

  const albumSource = albumSlug ? slugResult.albumSource : paramSource;
  const sourceId = albumSlug ? slugResult.sourceId : paramId;
  const albumInfo = albumSlug ? slugResult.data : directAlbumInfo;
  const albumLoading = albumSlug ? slugResult.isLoading : directLoading;
  useTrackPageView({
    pageType: PAGE_TYPE.REVIEWS_DETAIL,
    contentType: CONTENT_TYPE.REVIEW,
    contentId: reviewId,
    contentName: albumInfo?.title,
  });

  const albumUrl = albumSlug ? `/reviews/${albumSlug}` : `/reviews/${albumSource}/${sourceId}`;

  const reviewIdNum = parseInt(reviewId, 10);
  const { data: review, isFetched: reviewFetched } = useReviewById(reviewIdNum);
  const deleteReviewMutation = useDeleteReview(albumSource, sourceId);
  const toggleLike = useToggleReviewLike(albumSource, sourceId);
  const toggleReviewBookmark = useToggleReviewBookmark(albumSource, sourceId);

  const queryClient = useQueryClient();
  const [comments, setComments] = useState(null);

  const invalidateReviewsCache = () => {
    queryClient.invalidateQueries({ queryKey: ['albumReviews', albumSource, sourceId] });
  };

  const isMyReview = user && review && user.id === review.user.id;
  const visibleGenres = albumInfo?.genres?.filter(Boolean).slice(0, 3) || [];
  const reviewDetailPath = albumSlug
    ? `/reviews/${albumSlug}/user-reviews/${reviewId}`
    : `/reviews/${albumSource}/${sourceId}/user-reviews/${reviewId}`;
  const reviewShareTitle =
    albumInfo && review
      ? `${review.user.nickname}님의 ${albumInfo.title} 리뷰`
      : undefined;

  const renderReviewAuthor = () => {
    if (!review) return null;

    return (
      <Box
        component="button"
        type="button"
        onClick={() =>
          navigateWithScrollMemory(`/users/${encodeURIComponent(review.user.nickname)}`)
        }
        sx={{
          appearance: 'none',
          border: 0,
          p: 0,
          background: 'transparent',
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.875, md: 1.25 },
          minWidth: 0,
          maxWidth: '100%',
          width: '100%',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Avatar
          src={review.user.profileImageUrl || defaultUserThumbnail}
          sx={{
            width: { xs: 32, md: 36 },
            height: { xs: 32, md: 36 },
            border: `1px solid ${theme.palette.grey.line}`,
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              maxWidth: '100%',
              width: 'fit-content',
            }}
          >
            <Typography
              sx={{
                flex: '0 1 auto',
                minWidth: 0,
                maxWidth: '100%',
                fontSize: { xs: 13, md: 14 },
                fontWeight: 700,
                color: theme.palette.black[6],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.25,
              }}
            >
              {review.user.nickname}
            </Typography>
            <GradeIcon grade={review.user.grade} size={{ xs: 13, md: 14 }} />
          </Box>
          <RatingReadOnly
            value={review.rating}
            variant={isMdUp ? 'fullResponsive' : 'small'}
            sx={{ mt: 0.25 }}
          />
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    if (reviewFetched && !review) {
      navigate(albumUrl, { replace: true });
    }
  }, [reviewFetched, review, albumUrl, navigate]);

  const {
    isEditing: isEditingMyReview,
    editFormData,
    setEditFormData,
    handleEdit: handleEditReview,
    handleCancel: handleCancelEdit,
    handleSubmit: handleSubmitEdit,
    handleDelete: handleDeleteMyReview,
  } = useMyReviewActions(review, { redirectAfterDelete: true }, { albumSource, sourceId });

  const handleDeleteReview = async () => {
    if (!review) return;
    await deleteReviewMutation.mutateAsync({ albumId: review.albumId, reviewId: review.id });
    navigate(albumUrl);
  };

  const handleLikeToggle = async () => {
    if (!isLogin) {
      saveCurrentPathAndRedirectToLoginWithScroll(navigateWithScrollMemory);
      return;
    }
    if (!review) return;
    try {
      await toggleLike.mutateAsync({ reviewId: review.id, isLiked: review.isLiked });
      if (!review.isLiked) {
        trackContentLike(
          {
            contentType: CONTENT_TYPE.REVIEW,
            contentId: review.id,
            pageType: PAGE_TYPE.REVIEWS_POST,
          },
          user,
        );
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  const handleReviewBookmarkToggle = async () => {
    if (!isLogin) {
      saveCurrentPathAndRedirectToLoginWithScroll(navigateWithScrollMemory);
      return;
    }
    if (!review) return;
    try {
      await toggleReviewBookmark.mutateAsync({
        reviewId: review.id,
        isBookmarked: review.isBookmarked,
      });
    } catch (err) {
      console.error('Failed to toggle review bookmark:', err);
    }
  };

  const renderReviewLikeActionButton = () => {
    if (!review) return null;
    const FavoriteIcon = review.isLiked ? Favorite : FavoriteBorderOutlined;

    return (
      <IconButton
        disableRipple
        onClick={handleLikeToggle}
        aria-label="toggle favorite"
        sx={{
          width: { xs: 32, md: 40 },
          height: { xs: 32, md: 40 },
          p: 1,
          border: `1px solid ${theme.palette.grey.line}`,
          borderRadius: 1,
        }}
      >
        {isDarkMode ? (
          <FavoriteIcon
            sx={{
              fontSize: { xs: 18, md: 24 },
              color: review.isLiked ? '#f16438 !important' : '#ffffff !important',
            }}
          />
        ) : (
          <Box
            component="img"
            src={review.isLiked ? favoriteIcon : favoriteOutlinedIcon}
            alt="favorite icon"
            sx={{
              width: { xs: 16, md: 24 },
              height: { xs: 16, md: 24 },
            }}
          />
        )}
      </IconButton>
    );
  };

  const renderReadonlyReviewStats = () => {
    if (!review) return null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            component="img"
            src={review.isLiked ? favoriteIcon : favoriteOutlinedIcon}
            alt="favorite icon"
            sx={{
              width: { xs: 14, md: 16 },
              height: { xs: 14, md: 16 },
              transform: { xs: 'translateY(-1px)', md: 'none' },
            }}
          />
          <Typography sx={{ fontSize: 14, color: theme.palette.black[4], whiteSpace: 'nowrap' }}>
            {review.likeCount ?? 0}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Bookmark
            sx={{
              fontSize: { xs: 14, md: 16 },
              color: theme.palette.black[3],
              transform: { xs: 'translateY(-1px)', md: 'none' },
            }}
          />
          <Typography sx={{ fontSize: 14, color: theme.palette.black[4], whiteSpace: 'nowrap' }}>
            {review.bookmarkCount ?? 0}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderReviewActions = ({ includeLike = false } = {}) => {
    if (!review || (isMyReview && isEditingMyReview)) return null;

    return (
      <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
        <ShareButton
          url={reviewDetailPath}
          contentType={CONTENT_TYPE.REVIEW}
          contentId={review.id}
          contentName={albumInfo?.title}
          shareTitle={reviewShareTitle}
          shareText={reviewShareTitle}
          pageType={PAGE_TYPE.REVIEWS_DETAIL}
        />
        {includeLike && renderReviewLikeActionButton()}
        <BookmarkButton isBookmarked={review.isBookmarked} onToggle={handleReviewBookmarkToggle} />
        {isAdmin && !isMyReview && <AdminDeleteButton onDelete={handleDeleteReview} />}
        {isMyReview && (
          <EditDeleteMenuButton onEdit={review.isHistorical ? undefined : handleEditReview} onDelete={handleDeleteMyReview} />
        )}
      </Stack>
    );
  };

  // 댓글을 CommentSection용 API로 별도 fetch (replies 중첩 구조)
  useEffect(() => {
    if (!reviewIdNum) return;

    const fetchComments = async () => {
      try {
        const { data } = await commentApi.getComments('album_review', reviewIdNum);
        setComments(data);
      } catch (err) {
        console.error('Failed to fetch comments:', err);
      }
    };
    fetchComments();
  }, [reviewIdNum]);

  return (
    <PageLayout maxWidth={'980px'}>
      {review && albumInfo && (
        <>
          <OpenInAppBanner
            type="review"
            title={`${review.user.nickname}님의 리뷰`}
            subtitle={`${albumInfo.artist.name} · ${albumInfo.title}`}
            image={albumInfo.imageUrl}
          />
          <SEO
            title={`${albumInfo.artist.name} - ${albumInfo.title} 리뷰 by ${review.user.nickname}`}
            description={review.content?.substring(0, 160)}
            image={albumInfo.imageUrl}
            url={
              albumSlug
                ? `${SITE_URL}/reviews/${albumSlug}/user-reviews/${reviewId}`
                : `${SITE_URL}/reviews/${albumSource}/${sourceId}/user-reviews/${reviewId}`
            }
          />
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Review',
                name: `${albumInfo.title} review by ${review.user.nickname}`,
                reviewBody: review.content,
                datePublished: review.createdAt
                  ? review.createdAt.replace(
                      /(\d+)\.(\d+)\.(\d+)/,
                      (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                    )
                  : undefined,
                author: {
                  '@type': 'Person',
                  name: review.user.nickname,
                  url: `${SITE_URL}/users/${encodeURIComponent(review.user.nickname)}`,
                },
                itemReviewed: {
                  '@type': 'MusicAlbum',
                  name: albumInfo.title,
                  byArtist: {
                    '@type': 'MusicGroup',
                    name: albumInfo.artist.name,
                  },
                  image: albumInfo.imageUrl,
                },
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: review.rating,
                  bestRating: 5,
                  worstRating: 0.5,
                },
              })}
            </script>
          </Helmet>
          <Helmet>
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Reviews',
                    item: `${SITE_URL}/reviews`,
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: albumInfo.title,
                    item: albumSlug
                      ? `${SITE_URL}/reviews/${albumSlug}`
                      : `${SITE_URL}/reviews/${albumSource}/${sourceId}`,
                  },
                  { '@type': 'ListItem', position: 4, name: 'UserReview' },
                ],
              })}
            </script>
          </Helmet>
        </>
      )}
      <ProgressBar isLoading={albumLoading}>
        <SidebarLayout pageType={PAGE_TYPE.REVIEWS_DETAIL}>
          {albumInfo && albumInfo.artist && (
            <>
              {/* 앨범 정보 헤더 - MD/LG */}
              {isMdUp && (
                <>
                  <Box sx={{ display: 'flex', gap: { md: '18px', lg: '22px' } }}>
                    <Box
                      sx={{
                        width: { md: '22%', lg: 170 },
                        minWidth: 168,
                        maxWidth: { lg: 170 },
                        flexShrink: 0,
                      }}
                    >
                      <AlbumCover
                        imageUrl={albumInfo.imageUrl}
                        altText={albumInfo.title}
                        isStarred={albumInfo.isStarred}
                        onClick={() => navigate(albumUrl)}
                        sx={{ width: '100%', aspectRatio: '1 / 1' }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: { md: '12px', lg: '30px' },
                        }}
                      >
                        <Box sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: '100%' }}>
                          <AlbumTitleLink
                            title={albumInfo.title}
                            to={albumUrl}
                            contentRating={albumInfo.contentRating}
                            lineClamp={'unset'}
                            sx={{
                              fontSize: { md: 14, lg: 18 },
                              fontWeight: 'bold',
                            }}
                            iconSx={{
                              width: '0.72em',
                              height: '0.72em',
                              marginLeft: '0.28em',
                              verticalAlign: '0.02em',
                            }}
                          />
                          <ArtistLink
                            artistName={albumInfo.artist.name}
                            artistSource={albumInfo.artist.artistSource}
                            artistSourceId={albumInfo.artist.sourceId}
                            artistSlug={albumInfo.artist.slug}
                            artistDbId={albumInfo.artist.id}
                            sx={{
                              fontSize: { md: 13, lg: 16 },
                              mt: { md: '10px', lg: '14px' },
                            }}
                          />
                          {visibleGenres.length > 0 && (
                            <Typography
                              sx={{
                                mt: { md: '10px', lg: '14px' },
                                fontSize: { md: 13, lg: 16 },
                                color: theme.palette.black[4],
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word',
                                lineHeight: 1.4,
                              }}
                              title={visibleGenres.join(', ')}
                            >
                              {visibleGenres.join(', ')}
                            </Typography>
                          )}
                        </Box>
                        {isMdUp && renderReviewActions({ includeLike: true })}
                      </Box>
                      {review && !(isMyReview && isEditingMyReview) && (
                        <Box mt={{ md: '20px', lg: '24px' }}>{renderReviewAuthor()}</Box>
                      )}
                    </Box>
                  </Box>
                  {review && !(isMyReview && isEditingMyReview) && (
                    <>
                      <Box sx={{ mt: { md: '20px', lg: '24px' } }}>
                        <Typography
                          fontSize={14}
                          color={theme.palette.black[6]}
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            lineHeight: 1.5,
                          }}
                        >
                          {review.content}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                        <Typography fontSize={14} color={theme.palette.black[1]}>
                          {review.updatedAt}
                        </Typography>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: '8px',
                            alignSelf: 'center',
                            backgroundColor: theme.palette.grey.line,
                          }}
                        />
                        {renderReadonlyReviewStats()}
                      </Box>
                    </>
                  )}
                  {review && isMyReview && isEditingMyReview && (
                    <Box mt={'30px'}>
                      <MyReviewForm
                        myReview={review}
                        onSubmit={handleSubmitEdit}
                        onCancel={handleCancelEdit}
                        isEditing
                        editFormData={editFormData}
                        setEditFormData={setEditFormData}
                      />
                    </Box>
                  )}
                </>
              )}

              {/* 앨범 정보 헤더 - XS */}
              {!isMdUp && review && (
                <>
                  <Box sx={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
                    <Box
                      sx={{
                        width: '42%',
                        minWidth: 128,
                        maxWidth: 168,
                        flexShrink: 0,
                      }}
                    >
                      <AlbumCover
                        imageUrl={albumInfo.imageUrl}
                        altText={albumInfo.title}
                        isStarred={albumInfo.isStarred}
                        onClick={() => navigate(albumUrl)}
                        sx={{ width: '100%', aspectRatio: '1 / 1' }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        transform: 'translateY(-4px)',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '10px',
                          mb: 0,
                        }}
                      >
                        <Box sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: '100%' }}>
                          <AlbumTitleLink
                            title={albumInfo.title}
                            to={albumUrl}
                            contentRating={albumInfo.contentRating}
                            lineClamp={'unset'}
                            sx={{ fontSize: 14, fontWeight: 'bold' }}
                            iconSx={{
                              width: '0.72em',
                              height: '0.72em',
                              marginLeft: '0.28em',
                              verticalAlign: '0.02em',
                            }}
                          />
                          <ArtistLink
                            artistName={albumInfo.artist.name}
                            artistSource={albumInfo.artist.artistSource}
                            artistSourceId={albumInfo.artist.sourceId}
                            artistSlug={albumInfo.artist.slug}
                            artistDbId={albumInfo.artist.id}
                            sx={{ fontSize: 13, mt: 0.75 }}
                          />
                          {visibleGenres.length > 0 && (
                            <Typography
                              sx={{
                                mt: 0.75,
                                fontSize: 13,
                                color: theme.palette.black[4],
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word',
                                lineHeight: 1.4,
                              }}
                              title={visibleGenres.join(', ')}
                            >
                              {visibleGenres.join(', ')}
                            </Typography>
                          )}
                        </Box>
                        {isMdUp && renderReviewActions()}
                      </Box>
                      {!(isMyReview && isEditingMyReview) && (
                        <Box sx={{ mt: '10px', width: '100%', minWidth: 0, maxWidth: '100%' }}>
                          {renderReviewAuthor()}
                          <Box sx={{ mt: '10px' }}>{renderReviewActions({ includeLike: true })}</Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {/* 리뷰 내용 또는 수정 폼 */}
                  {isMyReview && isEditingMyReview ? (
                    <Box sx={{ mt: '30px' }}>
                      <MyReviewForm
                        myReview={review}
                        onSubmit={handleSubmitEdit}
                        onCancel={handleCancelEdit}
                        isEditing
                        editFormData={editFormData}
                        setEditFormData={setEditFormData}
                      />
                    </Box>
                  ) : (
                    <>
                      {/* 리뷰 내용 - 전체 너비 */}
                      <Box sx={{ mt: '16px' }}>
                        <Typography
                          fontSize={14}
                          color={theme.palette.black[6]}
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            lineHeight: 1.5,
                          }}
                        >
                          {review.content}
                        </Typography>
                      </Box>
                      {/* 날짜 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                        <Typography fontSize={14} color={theme.palette.black[1]}>
                          {review.updatedAt}
                        </Typography>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: '8px',
                            alignSelf: 'center',
                            backgroundColor: theme.palette.grey.line,
                          }}
                        />
                        {renderReadonlyReviewStats()}
                      </Box>
                    </>
                  )}
                </>
              )}

              {/* 광고 */}
              <Box mt={{ xs: '30px', md: '40px', lg: '30px' }}>
                <InFeedBoxAd pageType={PAGE_TYPE.REVIEWS_DETAIL} />
              </Box>

              {/* Comments 섹션 */}
              {review && comments !== null && (
                <Box mt={{ xs: '30px', md: '40px', lg: '30px' }}>
                  <CommentSection
                    commentData={comments}
                    articleType="album_review"
                    articleId={reviewIdNum}
                    contentName={albumInfo?.title}
                    commentScrollId={commentScrollId}
                    onSubmit={async (content) => {
                      const { data } = await reviewsApi.addComment(reviewIdNum, content);
                      invalidateReviewsCache();
                      return data;
                    }}
                    onReply={async (parentId, content, mentionedUserId) => {
                      const { data } = await reviewsApi.addComment(
                        reviewIdNum,
                        content,
                        parentId,
                        mentionedUserId,
                      );
                      invalidateReviewsCache();
                      return data;
                    }}
                    onDelete={() => invalidateReviewsCache()}
                  />
                </Box>
              )}
            </>
          )}
        </SidebarLayout>
      </ProgressBar>
    </PageLayout>
  );
};

export default UserReview;
